/**
 * Tool Call Rollup.
 *
 * Renders every tool invocation of an assistant turn as ONE compact,
 * collapsible card instead of a separate scrolling line per call/result.
 * Collapsed by default: shows a count (and the currently running tool while
 * streaming); expanding reveals per-call details and result summaries.
 */

import React, { useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Wrench,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ToolCallEvent } from '@/types/chat-agent-types';

export interface ToolCallRollupProps {
  events: ToolCallEvent[];
  className?: string;
}

const StatusIcon: React.FC<{ status: ToolCallEvent['status'] }> = ({
  status,
}) => {
  if (status === 'running') {
    return (
      <Loader2 className='size-3 shrink-0 animate-spin text-muted-foreground' />
    );
  }
  if (status === 'failed') {
    return <X className='size-3 shrink-0 text-red-500' />;
  }
  return (
    <Check className='size-3 shrink-0 text-emerald-600 dark:text-emerald-400' />
  );
};

export const ToolCallRollup: React.FC<ToolCallRollupProps> = ({
  events,
  className,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (events.length === 0) return null;

  const running = events.filter((e) => e.status === 'running');
  const failedCount = events.filter((e) => e.status === 'failed').length;
  const isActive = running.length > 0;
  const currentTool = running[running.length - 1];

  const headerLabel = isActive
    ? `Running ${currentTool.name}...`
    : `${events.length} tool call${events.length !== 1 ? 's' : ''}`;

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-zinc-50 dark:bg-zinc-900',
        className,
      )}
    >
      <button
        type='button'
        onClick={() => setExpanded((v) => !v)}
        className='flex w-full items-center gap-2 px-3 py-2 text-left'
        aria-expanded={expanded}
      >
        {isActive ? (
          <Loader2 className='size-3.5 shrink-0 animate-spin text-zinc-500 dark:text-zinc-400' />
        ) : (
          <Wrench className='size-3.5 shrink-0 text-zinc-500 dark:text-zinc-400' />
        )}
        <span className='min-w-0 flex-1 truncate text-xs font-medium text-zinc-600 dark:text-zinc-400'>
          {headerLabel}
          {isActive && events.length > 1 && (
            <span className='font-normal text-zinc-400 dark:text-zinc-500'>
              {' '}
              · {events.length} call{events.length !== 1 ? 's' : ''}
            </span>
          )}
          {failedCount > 0 && (
            <span className='font-normal text-red-500'>
              {' '}
              · {failedCount} failed
            </span>
          )}
        </span>
        {expanded ? (
          <ChevronDown className='size-3.5 shrink-0 text-zinc-400 dark:text-zinc-500' />
        ) : (
          <ChevronRight className='size-3.5 shrink-0 text-zinc-400 dark:text-zinc-500' />
        )}
      </button>

      {expanded && (
        <div className='border-t border-border px-3 pb-2 pt-1.5'>
          <ul className='space-y-1'>
            {events.map((e, i) => (
              <li key={i} className='flex min-w-0 items-start gap-1.5'>
                <span className='flex h-4 items-center'>
                  <StatusIcon status={e.status} />
                </span>
                <div className='min-w-0 flex-1 text-xs'>
                  <span className='font-medium text-zinc-600 dark:text-zinc-300'>
                    {e.name}
                  </span>
                  {e.detail && (
                    <span className='text-zinc-400 dark:text-zinc-500'>
                      {' '}
                      — {e.detail}
                    </span>
                  )}
                  {e.summary && (
                    <span
                      className={cn(
                        e.status === 'failed'
                          ? 'text-red-500'
                          : 'text-zinc-400 dark:text-zinc-500',
                      )}
                    >
                      {' '}
                      · {e.summary}
                    </span>
                  )}
                  {e.url && (
                    <>
                      {' '}
                      <a
                        href={e.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-blue-600 underline underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
                      >
                        View workflow
                      </a>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ToolCallRollup;
